// src/components/chat/hooks/useChatAnalysis.ts

import { useState, useEffect, useMemo, useRef } from "react";
import { useSession } from "../../../contexts/SessionContext";
import { useModel } from "../../../contexts/ModelContext";
import { useChatSettings } from "../../../contexts/ChatSettingsContext";
import { useEntitlements } from "../../../hooks/useEntitlements";
import { useDocumentSelection } from "./useDocumentSelection";
import { ModelRequest, ModelRequestPreferences } from "../../../interfaces/modelService";
import { ChatMessage, InlineToolEvent, UserMessageAction } from "../../../interfaces/chatSession";
import { WebSearchEvent, WebSearchState } from "../../../interfaces/webSearchTypes";
import { WebFetchToolEvent, WebFetchState } from "../../../interfaces/webToolTypes";
import {
  WebSearchQuery,
  WebSearchResult,
  WebFetchResult,
  ServerToolUse,
  FormRequestPayload,
  NormalizedPlaybookResult,
} from "../../../interfaces/chatTypes";
import {
  PlaybookSelectionOption,
  PlaybookSelectionResult,
} from "../../../interfaces/playbookTypes";
import { AutoRedlineOutput } from "../../../interfaces/autoRedlineTypes";
import { AddCommentOutput } from "../../../interfaces/addCommentTypes";
import { DocumentCitation } from "../../../interfaces/documentCitationTypes";
import { WebCitationInsertion } from "../../../interfaces/webCitationTypes";
import { applyWebCitations } from "../../../utils/webCitations";
import {
  NormalizedCatylexSearchResult,
  CatylexToolInput,
} from "../../../interfaces/catylexSearchTypes";
import {
  NormalizedSharePointResult,
  SharePointToolInput,
} from "../../../interfaces/sharepointSearchTypes";
import { OutlookMailResult, OutlookMailToolInput } from "../../../interfaces/outlookMailTypes";
import { useDocumentAttachment } from "../../../hooks/useDocumentAttachment";
import { clearDocumentIndexTracker } from "../../autoRedline/services/wordSequentialEditService";
import { registerBaselineAnchor } from "../../autoRedline/services/appliedParagraphLedger";
import { agentModelServiceFactory } from "../../../services/agentModelServiceFactory";
import { createLogger } from "../../../utils/logger";
import { modifyJwtTenantId } from "../../../utils/jwtUtils";
import { useAuth } from "../../../contexts/AuthContext";
import { useAgent } from "../../../contexts/AgentContext";
import { track } from "../../../utils/analytics";
import { FEATURE_FLAGS } from "../../../config/featureFlags";
import { useTenantSelectorAccess } from "../../../hooks/useTenantSelectorAccess";

/**
 * Find or create a webTools InlineToolEvent at the given position.
 * If an event already exists at that position, returns it.
 * Otherwise creates a new one, pushes it to the array, and returns it.
 */
function findOrCreateWebToolsEvent(
  inlineToolEvents: InlineToolEvent[],
  position: number,
  messageId: string
): InlineToolEvent {
  const existing = inlineToolEvents.find((e) => e.type === "webTools" && e.position === position);
  if (existing) return existing;

  const event: InlineToolEvent = {
    id: `webTools-${messageId}-pos-${position}`,
    type: "webTools",
    position,
    timestamp: Date.now(),
    data: {
      webSearchEvents: [],
      webFetchEvents: [],
    },
  };
  inlineToolEvents.push(event);
  return event;
}

/**
 * Recover a contract's "View in Catylex" URL (all_terms_report_url) from earlier
 * turns of the session.
 *
 * That URL is only ever issued by a search response — neither the all-terms
 * report the read is sourced from nor /contract/metadata returns it — and the
 * backend's read cache is request-scoped. So
 * on the dominant flow — search in turn 1, "now load the Zeus one" in turn 2 —
 * the backend has nothing to send and correctly emits "". The frontend is then
 * the only place the URL still exists: it is sitting in the turn-1 search result
 * in session state.
 *
 * Scans newest-first and returns the first match, or "" when the contract never
 * appeared in an earlier search (the card then simply omits the link, as before).
 */
export function findAtrUrlInSession(messages: ChatMessage[], contractId: string): string {
  if (!contractId) return "";

  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i];

    // Both copies are read because they do not always both survive: a live
    // message carries the same result object in each, but a message restored
    // from a stored conversation keeps only the inline tool event, which is the
    // authoritative render and history source.
    const results = [
      ...(message.catylexSearchResults ?? []),
      ...(message.inlineToolEvents ?? []).flatMap((event) =>
        event.data.catylexSearchResult ? [event.data.catylexSearchResult] : []
      ),
    ];

    for (const result of results) {
      const contractHit = result.contracts?.find((c) => c.id === contractId);
      if (contractHit?.all_terms_report_url) return contractHit.all_terms_report_url;

      const sentenceHit = result.sentences?.find((s) => s.contract_id === contractId);
      if (sentenceHit?.all_terms_report_url) return sentenceHit.all_terms_report_url;

      // An earlier read of the same contract that already recovered the URL.
      if (result.contractId === contractId && result.allTermsReportUrl) {
        return result.allTermsReportUrl;
      }
    }
  }

  return "";
}

/**
 * Check if an error indicates a context window overflow (prompt too large).
 * Used for PostHog analytics only — no UI impact.
 */
function isContextWindowOverflow(error: any): boolean {
  if (error?.errorCode === "TOKEN_LIMIT") return true;
  // Fallback: pattern match for backward compat during rollout
  const msg = (error?.message || "").toLowerCase();
  return msg.includes("too large") || msg.includes("too long");
}

/**
 * Options for the useChatAnalysis hook
 */
interface UseChatAnalysisOptions {
  /**
   * Callback invoked when generation is cancelled before any main content appears.
   * The user's prompt is passed back so it can be restored to the input field.
   */
  onPromptRestore?: (prompt: string) => void;
}

/**
 * Information about a pending assistant message (for cancellation detection)
 * Used to track multiple pending messages when agents run in parallel
 */
interface PendingMessageInfo {
  messageId: string;
  agentId: string;
  userPrompt: string;
  timestamp: number;
}

/**
 * Hook to manage chat analysis state and operations with Enhanced Thinking support
 *
 * This enhanced version maintains full backward compatibility while adding support
 * for user-controlled Extended Thinking functionality. The hook now coordinates
 * between user preferences, document context, and AI model services to deliver
 * a seamless experience that respects user choices.
 *
 * Enhanced with improved conversation history timing, session coordination,
 * and thinking state management for the UI layer.
 */
/**
 * Sent as the current message when the user picks a playbook from the card (the
 * user types nothing). The chosen playbook is carried as the tool_result that
 * closes the list call in history; this neutral note nudges the model to
 * continue with the selected playbook.
 */
const PLAYBOOK_RESUME_NOTE =
  "[The user selected a playbook from the list shown above. Load it with get_client_playbook and continue with their original request.]";

export const useChatAnalysis = (options?: UseChatAnalysisOptions) => {
  const { onPromptRestore } = options || {};
  // Create a logger specifically for chat analysis operations
  const log = createLogger("ChatAnalysis");

  // Local UI state
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [directResponseText, setDirectResponseText] = useState<string | null>(null);
  const [lastResponseText, setLastResponseText] = useState<string | null>(null);

  // Track pending messages per agent for cancellation detection
  // Using Map allows multiple agents to run in parallel without overwriting each other
  const [pendingMessages, setPendingMessages] = useState<Map<string, PendingMessageInfo>>(
    new Map()
  );

  // Context hooks - enhanced with settings coordination
  const {
    currentSession,
    addUserMessageForAgent,
    addAssistantMessageForAgent,
    updateMessage,
    updateMessageForAgent,
    clearConversation,
    removeLastMessagePair,
    removeLastMessagePairForAgent,
    getAgentSession,
    getConversationHistory,
    setSelectedPlaybook,
    getSelectedPlaybook,
    registerStreamingProbe,
    deleteStoredSession,
  } = useSession();
  const { generateStreamingResponse, isGenerating, isGeneratingForAgent, clearSession } =
    useModel();
  const {
    settings,
    isExtendedThinkingEnabled,
    isWebSearchEnabled,
    isBenchmarkModeEnabled,
    isConciseModeEnabled,
    getTenantId,
  } = useChatSettings();

  const { conciseMode: isConciseModeAvailable } = useEntitlements();
  const documentSelection = useDocumentSelection();
  const { currentAgent } = useAgent();
  const { handleOutlookMailResult: updateAttachmentState, getAttachmentDetailsIfNeeded } =
    useDocumentAttachment();

  // Add auth context
  const { accessToken, getFreshAccessToken, isLoading: authLoading } = useAuth();

  // Check if current user has access to tenant selector
  const tenantSelectorAllowed = useTenantSelectorAccess();

  // Hand the session layer a way to ask whether an agent is generating. It
  // persists conversations on a debounce and must not write a half-streamed
  // turn, but it is mounted above ModelContext and cannot read the state
  // itself. The probe reads through a ref so the registration survives the
  // re-render each streaming chunk causes.
  const isGeneratingForAgentRef = useRef(isGeneratingForAgent);
  useEffect(() => {
    isGeneratingForAgentRef.current = isGeneratingForAgent;
  });
  useEffect(() => {
    registerStreamingProbe((agentId: string) => isGeneratingForAgentRef.current(agentId));
    return () => registerStreamingProbe(null);
  }, [registerStreamingProbe]);

  // Combine errors from document selection with local errors
  useEffect(() => {
    if (documentSelection.error) {
      setError(documentSelection.error);
    }
  }, [documentSelection.error]);

  // Detect cancellation - handles multiple agents running in parallel
  // Each agent's pending message is tracked independently in the Map
  useEffect(() => {
    if (pendingMessages.size === 0) return;

    // Collect agents to remove after processing (can't modify during forEach)
    const agentsToRemove: string[] = [];

    // Check each pending message independently
    pendingMessages.forEach((pendingInfo, agentId) => {
      // Check if THIS specific agent is still generating
      const isThisAgentGenerating = isGeneratingForAgent(agentId);

      if (!isThisAgentGenerating) {
        // This agent has stopped generating - determine why and handle appropriately
        const session = getAgentSession(agentId);
        if (!session) {
          log.warn("Agent session not found during cancellation check", { agentId });
          agentsToRemove.push(agentId);
          return;
        }

        const lastMessage = session.messages.find((msg) => msg.id === pendingInfo.messageId);

        const hasThinkingSegmentWithContent = (lastMessage?.inlineToolEvents ?? []).some(
          (evt) => evt.type === "thinking" && (evt.data.thinking?.content?.length ?? 0) > 0
        );

        if (lastMessage && !lastMessage.content && !hasThinkingSegmentWithContent) {
          // Message was truly cancelled (no content and no thinking), remove the Q&A pair
          log.info("Detected cancelled generation, removing last message pair", {
            messageId: pendingInfo.messageId,
            agentId: pendingInfo.agentId,
            isCurrentAgent: agentId === currentAgent.id,
          });

          // Remove the Q&A pair from this agent's session
          removeLastMessagePairForAgent(agentId);

          // Restore prompt ONLY if this is the currently active/visible agent
          if (agentId === currentAgent.id && onPromptRestore && pendingInfo.userPrompt) {
            log.info("Restoring prompt for active agent", {
              agentId,
              promptLength: pendingInfo.userPrompt.length,
            });
            onPromptRestore(pendingInfo.userPrompt);
          }
        } else if (lastMessage) {
          // Message has content (text and/or thinking) but was interrupted —
          // mark it so the history serializer adds the [interrupted] suffix.
          log.info("Marking message as interrupted", {
            messageId: pendingInfo.messageId,
            agentId,
            contentLength: lastMessage.content.length,
            hasThinking: hasThinkingSegmentWithContent,
          });

          // This is the ONLY place a user-initiated stop lands: the API layer
          // returns on AbortError without calling onComplete or onError, so any
          // in-flight tool state left set by onToolUseStart / on*ToolInput would
          // otherwise stay set for the life of the transcript — the processing
          // card is gated on isProcessingTool alone and keeps its 1s interval
          // ticking "Still processing..." forever. Clear it here, for all three
          // tools (a slow Catylex read is just where it became reachable).
          updateMessageForAgent(agentId, lastMessage.id, {
            wasInterrupted: true,
            isProcessingTool: false,
            catylexToolInput: undefined,
            sharepointToolInput: undefined,
            outlookMailToolInput: undefined,
          });
        }

        // Mark this agent's pending message for removal
        agentsToRemove.push(agentId);
      }
    });

    // Remove processed agents from pending messages
    if (agentsToRemove.length > 0) {
      setPendingMessages((prev) => {
        const newMap = new Map(prev);
        agentsToRemove.forEach((agentId) => newMap.delete(agentId));
        return newMap;
      });
    }
  }, [
    pendingMessages,
    currentAgent.id,
    isGeneratingForAgent,
    getAgentSession,
    removeLastMessagePairForAgent,
    updateMessageForAgent,
    onPromptRestore,
  ]);

  // Reset busy state when generation stops (handles cancellation case)
  useEffect(() => {
    if (!isGenerating && busy) {
      log.debug("Resetting busy state after generation stopped", {
        wasGenerating: false,
        wasBusy: true,
      });
      setBusy(false);
    }
  }, [isGenerating, busy]);

  // Extract and cache the latest assistant message for UI display
  useEffect(() => {
    const lastMessage = [...currentSession.messages]
      .reverse()
      .find((msg) => msg.role === "assistant");

    if (lastMessage && lastMessage.content) {
      log.debug("Latest assistant message updated", {
        messageId: lastMessage.id,
        contentLength: lastMessage.content.length,
        preview: lastMessage.content.substring(0, 50) + "...",
      });
      setLastResponseText(lastMessage.content);
    }
  }, [currentSession.messages]);

  // Memoized latest assistant message for performance
  const latestAssistantMessage = useMemo<ChatMessage | null>(() => {
    return [...currentSession.messages].reverse().find((msg) => msg.role === "assistant") || null;
  }, [currentSession.messages]);

  /**
   * Prepare user preferences from current settings state
   * This method translates UI settings into the format expected by the service layer
   */
  const prepareUserPreferences = useMemo((): ModelRequestPreferences => {
    const preferences: ModelRequestPreferences = {
      extendedThinking: isExtendedThinkingEnabled(),
      webSearch: isWebSearchEnabled(),
      benchmarkMode: isBenchmarkModeEnabled(),
      // Gated on the entitlement, not just the stored setting. The setting is
      // persisted in localStorage, so a user who switched concise mode on
      // before their tenant moved to a default response style would otherwise
      // keep sending it forever - with the toggle now hidden, and no way left
      // in the UI to turn it back off.
      conciseMode: isConciseModeEnabled() && isConciseModeAvailable,
      tenantId: getTenantId(),
      // NOTE: selectedPlaybookId is intentionally NOT set here. It is read live
      // at request-build time (see handleAnalyze) because a playbook can be
      // picked in the same render that sends the request, so a memoized value
      // could be out of date.
    };

    log.debug("Prepared user preferences for model request", {
      extendedThinking: preferences.extendedThinking,
      webSearch: preferences.webSearch,
      benchmarkMode: preferences.benchmarkMode,
      conciseMode: preferences.conciseMode,
      tenantId: preferences.tenantId,
      settingsState: settings,
    });

    return preferences;
  }, [
    settings,
    isExtendedThinkingEnabled,
    isWebSearchEnabled,
    isBenchmarkModeEnabled,
    isConciseModeEnabled,
    isConciseModeAvailable,
    getTenantId,
  ]);

  /**
   * Handle user message submission and AI response generation with Enhanced Thinking support
   *
   * This method has been enhanced to include user preferences in model requests,
   * enabling dynamic control over Extended Thinking based on user settings.
   *
   * The flow now includes preference preparation and per-message thinking state management
   * while maintaining full backward compatibility with existing functionality.
   *
   * @param inputMessage Optional message to use instead of prompt state
   */
  const handleAnalyze = async (
    inputMessage?: string,
    opts?: {
      resume?: boolean;
      onResumeFailure?: () => void;
      /** The add-in action that sent the message, where it was not typed. */
      action?: UserMessageAction;
    }
  ) => {
    // Check for access token
    if (!accessToken) {
      setError("Authentication required. Please refresh the page.");
      opts?.onResumeFailure?.();
      return;
    }

    // Use provided message or current prompt state. Continuation turns (a playbook
    // card was just answered) carry no user text — send the neutral note; the
    // user's choice lives in the tool_result already in conversation history.
    let messageToUse = opts?.resume ? PLAYBOOK_RESUME_NOTE : inputMessage?.trim() || prompt.trim();

    // Log the final message being sent to the backend (for debugging citation format)
    log.debug("📤 Raw message to backend", {
      rawMessage: messageToUse,
      originalMessage: inputMessage?.trim() || prompt.trim(),
      webSearchEnabled: isWebSearchEnabled(),
      benchmarkModeEnabled: FEATURE_FLAGS.ENABLE_BENCHMARK_MODE && isBenchmarkModeEnabled(),
    });

    // Validate input and prevent concurrent requests
    if (!messageToUse || isGenerating || busy || authLoading) {
      log.debug("Skipping analysis - validation failed", {
        hasMessage: !!messageToUse,
        isGenerating,
        busy,
        authLoading,
        hasAccessToken: !!accessToken,
      });
      return;
    }

    try {
      setBusy(true);
      setError(null);

      // CS-259: Clear document-level index tracker when sending a new message
      // This ensures each new AI response starts with a fresh tracker.
      // Without this, if user undoes changes in Word and asks for new edits,
      // the tracker would still have the old operations recorded.
      try {
        clearDocumentIndexTracker();
        log.debug("Document index tracker cleared for new message");
      } catch (trackerError) {
        log.warn("Could not clear document index tracker", {
          error: trackerError instanceof Error ? trackerError.message : String(trackerError),
        });
      }

      log.info("Starting chat analysis with Enhanced Thinking and Web Search support", {
        message: messageToUse.substring(0, 50) + (messageToUse.length > 50 ? "..." : ""),
        sessionMessageCount: currentSession.messages.length,
        extendedThinkingEnabled: prepareUserPreferences.extendedThinking,
        webSearchEnabled: prepareUserPreferences.webSearch,
        conciseModeEnabled: prepareUserPreferences.conciseMode,
      });

      // Client-side clock, started HERE rather than at the point the request is
      // built: reading the document out of Word, reading attachments and any
      // token refresh all happen first and can dominate the wait. This is
      // deliberately not the backend's $ai_latency - it is the whole time the
      // user sat there, which is the only side that can see it.
      const turnStartedAt = Date.now();

      // Enhanced timing: Capture conversation history BEFORE adding any new messages
      // This prevents potential timing issues with React state updates
      // IMPORTANT: Use getConversationHistory() to get augmented history with form summaries, etc.
      const existingConversationHistory = getConversationHistory();

      log.debug("Conversation history prepared", {
        historyLength: existingConversationHistory.length,
        messagesWithFormContext: existingConversationHistory.filter(
          (msg) =>
            typeof msg.content === "string" && msg.content.includes("[WORKFLOW FORM DISPLAYED:")
        ).length,
      });

      // Get document context using the simplified document selection hook
      // The service will handle converting this to files
      // TEMPORARY: Selected text feature disabled - requireSelection set to false
      // TODO: Re-enable by setting requireSelection back to true
      const documentContext = await documentSelection.getDocumentContext({
        includeFullDocument: true,
        requireSelection: false,
      });

      if (!documentContext) {
        // Error message is already set by the document selection hook
        setBusy(false);
        return;
      }

      log.debug("Document context prepared", {
        hasSelection: !!documentContext.currentSelection,
        hasFullDocument: !!documentContext.fullDocument,
        selectionLength: documentContext.currentSelection?.length || 0,
        fullDocumentLength: documentContext.fullDocument?.length || 0,
      });

      // Check if document attachment is pending (Outlook mail preview-confirm-send flow)
      // Done here (before messages are added) to avoid an async gap after message creation
      // that would trigger the cancellation detection useEffect
      const attachmentDetails = await getAttachmentDetailsIfNeeded();

      // Files riding on this request, read once and used twice: for the
      // send-time analytics below, and to record on the user's message which
      // files the turn was answered with. The bytes are gone when the pane
      // closes, so the names are the only trace a restored conversation keeps.
      // Never let reading them break a send.
      let uploadedFiles: File[] = [];
      try {
        const svc = agentModelServiceFactory.getServiceForAgent(currentAgent.id);
        if (svc && "getAdditionalFiles" in svc) {
          uploadedFiles = (svc as { getAdditionalFiles(): File[] }).getAdditionalFiles();
        }
      } catch {
        /* never fatal - this is metadata, not the request itself */
      }

      // The one send-time analytics event: everything about this query in a
      // single place (agent, parameters, document + attachment context).
      // Skipped for playbook resume continuations: those carry the synthetic
      // PLAYBOOK_RESUME_NOTE and add no user message, so counting them would
      // inflate message counts and collide message_index with the next real send.
      if (!opts?.resume) {
        const attachmentTypes = [
          ...uploadedFiles.map((f) => f.name.split(".").pop() || "unknown"),
          ...(attachmentDetails
            ? [attachmentDetails.open_document_name.split(".").pop() || "unknown"]
            : []),
        ];
        const attachmentBytes =
          uploadedFiles.reduce((sum, f) => sum + f.size, 0) +
          (attachmentDetails ? Math.round(attachmentDetails.open_document_bytes.length * 0.75) : 0);
        track("chat:message_sent", {
          agent_id: currentAgent.id,
          message_length: messageToUse.length,
          message_index: currentSession.messages.filter((m) => m.role === "user").length + 1,
          extended_thinking_enabled: prepareUserPreferences.extendedThinking ?? false,
          web_search_enabled: prepareUserPreferences.webSearch ?? false,
          concise_mode_enabled: prepareUserPreferences.conciseMode ?? false,
          composer_mode_enabled: settings.composerMode,
          benchmark_mode_enabled: prepareUserPreferences.benchmarkMode ?? false,
          track_changes_mode: documentContext.trackChangesMode || "unknown",
          markup_mode: documentContext.markupMode || "unknown",
          attachment_count: attachmentTypes.length,
          ...(attachmentTypes.length > 0
            ? { attachment_types: attachmentTypes, attachment_bytes_total: attachmentBytes }
            : {}),
          selected_playbook_id: getSelectedPlaybook?.()?.id,
          quick_analysis: opts?.action === "quick_analysis",
          draft_certificate: opts?.action === "draft_certificate",
        });
      }

      // Send-time token preflight: if the token expired while the taskpane was
      // idle (webview timer throttling starves the auto-refresh timer), refresh
      // it now so the request doesn't fail in the user's face. MUST also stay
      // before messages are added - a refresh can take seconds, and any await
      // after message creation opens the same cancellation-detection gap as
      // above (the pending message would be seen with generation not started
      // and wrongly removed as cancelled).
      const freshToken = (await getFreshAccessToken()) ?? accessToken;
      if (attachmentDetails) {
        log.info("Document attachment included in request", {
          documentName: attachmentDetails.open_document_name,
          bytesLength: attachmentDetails.open_document_bytes.length,
        });
      } else {
        log.info("No document attachment pending");
      }

      // CRITICAL: Capture the agent ID at message creation time
      // This ensures messages and callbacks go to the correct agent's session even after switching
      const capturedAgentId = currentAgent.id;

      // Add user message to the specific agent's session to prevent race conditions
      // The service layer will extract what it needs from this context.
      // Continuation turns add NO user bubble — the answered card is the user's
      // input, and its choice is already represented as the tool_result in history.
      if (!opts?.resume) {
        const userMessage = addUserMessageForAgent(
          capturedAgentId,
          messageToUse,
          {
            selectionOoxml: documentContext.currentSelection,
            documentOoxml: documentContext.fullDocument,
          },
          opts?.action
        );

        // Record which files this turn was answered with. Only the metadata
        // survives - the bytes go when the pane closes - but it is enough for a
        // restored conversation to name them rather than deny they existed.
        if (uploadedFiles.length > 0) {
          updateMessageForAgent(capturedAgentId, userMessage.id, {
            attachments: uploadedFiles.map((file) => ({
              name: file.name,
              size: file.size,
              type: file.name.includes(".")
                ? (file.name.split(".").pop() || "unknown").toLowerCase()
                : "unknown",
            })),
          });
        }
      }

      // Add placeholder assistant message to the specific agent's session
      // Initialize with thinking state fields ready
      const assistantMessage = addAssistantMessageForAgent(capturedAgentId, "");

      // Capture composer mode state at message creation time
      // Use agent-aware update to prevent race conditions during quick agent switches
      updateMessageForAgent(capturedAgentId, assistantMessage.id, {
        composerModeActiveOnCreation: settings.composerMode,
      });

      // Track this message for cancellation detection (per-agent, supports parallel execution)
      setPendingMessages((prev) => {
        const newMap = new Map(prev);
        newMap.set(capturedAgentId, {
          messageId: assistantMessage.id,
          agentId: capturedAgentId,
          userPrompt: messageToUse,
          timestamp: Date.now(),
        });
        return newMap;
      });

      // Modify access token with selected tenant ID when tenant selector is enabled
      let tokenToUse = freshToken;
      if (tenantSelectorAllowed && prepareUserPreferences.tenantId) {
        tokenToUse = modifyJwtTenantId(tokenToUse, prepareUserPreferences.tenantId);
        log.info("JWT token modified with tenant ID for testing", {
          tenantId: prepareUserPreferences.tenantId,
        });
      }

      // Enhanced: Prepare model request with user preferences
      const request: ModelRequest = {
        prompt: messageToUse,
        conversationHistory: existingConversationHistory, // Use captured history for consistency
        documentContext: {
          currentSelection: documentContext.currentSelection,
          fullDocument: documentContext.fullDocument,
          trackChangesMode: documentContext.trackChangesMode || "trackAll",
          markupMode: documentContext.markupMode || "unknown",
        },
        // Include user preferences for Enhanced Thinking
        preferences: {
          ...prepareUserPreferences,
          // Read the selected playbook LIVE at send time (not from the memo):
          // a playbook can be picked in the same render that sends the request,
          // so a memoized value could be out of date.
          selectedPlaybookId: getSelectedPlaybook?.()?.id,
          // This continuation turn carries the neutral PLAYBOOK_RESUME_NOTE, not
          // user-typed text. Flag it so the backend doesn't log the note as the
          // user's question. Authoritative per-turn signal (selectedPlaybookId
          // persists across later real turns, so it can't be used for this).
          isPlaybookResume: !!opts?.resume,
          // Per-turn on purpose: the backend only withholds tools and
          // pre-loads the playbook for the opening turn, and the shape of
          // every later answer is carried by the conversation history rather
          // than by re-sending this. The Draft Certificate sends no flag: its
          // prompt is self-contained and the backend serves it as any typed
          // message.
          ...(opts?.action === "quick_analysis" && { quickAnalysis: true }),
          // Outlook mail document attachment (only present when pendingDocumentAttachment is true)
          ...(attachmentDetails && {
            openDocumentBytes: attachmentDetails.open_document_bytes,
            openDocumentName: attachmentDetails.open_document_name,
          }),
        },
        // Add access token to request (potentially modified with tenant ID)
        accessToken: tokenToUse,
      };

      log.debug("Request prepared with Enhanced Thinking and Web Search preferences", {
        promptLength: request.prompt.length,
        conversationHistoryLength: request.conversationHistory.length,
        hasDocumentContext: !!request.documentContext,
        extendedThinking: request.preferences?.extendedThinking,
        webSearch: request.preferences?.webSearch,
        conciseMode: request.preferences?.conciseMode,
      });

      // Track streaming response for real-time updates
      let currentResponse = "";

      // Set when onError fires. Backend error chunks call onError but the SSE
      // loop still ends "normally" and onComplete runs afterwards - without
      // this flag such a turn would be double-counted (response_failed AND
      // response_interrupted).
      let streamErrorReported = false;

      // Backend LLM-analytics trace for this turn, arriving on the first
      // message_start. Carried onto the outcome events so a product event can
      // be opened as an LLM trace, which is the join the backend cannot make
      // on its own. Undefined when the backend has analytics off.
      let aiTraceId: string | undefined;

      let firstChunkAt: number | undefined;

      // Currently-streaming thinking segment as an inline event. Each thinking_start
      // begins a fresh segment positioned at the current character offset; chunks
      // accumulate live into its `content`; thinking_stop finalises it (signature +
      // isStreaming=false). The local closure also lets onError/onComplete close
      // out any segment that didn't finish.
      let activeThinkingEvent: InlineToolEvent | null = null;

      // NEW: Track web search state for this message
      const webSearchState: WebSearchState = {
        isSearching: false,
        currentSearchNumber: 0,
        searchEvents: [],
        pendingQuery: undefined,
      };

      // NEW: Track web fetch state for this message
      const webFetchState: WebFetchState = {
        isFetching: false,
        currentFetchNumber: 0,
        fetchEvents: [],
      };

      // Track queries by search number to handle multiple concurrent searches
      const queryBySearchNumber = new Map<number, string>();
      const searchesAwaitingQuery: number[] = []; // Queue: which searches are waiting for queries

      // NEW: Track auto-redline outputs for this message
      const autoRedlineOutputs: AutoRedlineOutput[] = [];

      // NEW: Track form request payload for this message
      let formRequestPayload: FormRequestPayload | null = null;

      // NEW: Track playbook result for this message
      let playbookResult: NormalizedPlaybookResult | null = null;

      // NEW: Track Catylex search results for this message
      const catylexSearchResults: NormalizedCatylexSearchResult[] = [];

      // NEW: Track SharePoint search results for this message
      const sharepointSearchResults: NormalizedSharePointResult[] = [];
      let currentSharePointInput: SharePointToolInput | undefined;

      // NEW: Track Outlook mail results for this message
      const outlookMailResults: OutlookMailResult[] = [];
      let currentOutlookMailInput: OutlookMailToolInput | undefined;

      // NEW: Track document citations for this message
      const documentCitationsMap: Record<string, DocumentCitation> = {};

      // Web citations rebuilt from Gemini's search grounding, spliced into the
      // answer once it is complete
      const webCitationInsertions: WebCitationInsertion[] = [];

      // NEW: Track all inline tool events for unified rendering
      // This array enables multiple tools to render at their correct positions
      const inlineToolEvents: InlineToolEvent[] = [];
      let inlineToolEventCounter = 0;

      // Track content positions for web search and web fetch tool events
      const webSearchPositionMap = new Map<number, number>(); // searchNumber → content position
      const webFetchPositionMap = new Map<number, number>(); // fetchToolUseNumber → content position
      let webFetchToolUseCount = 0;

      // Enhanced streaming callbacks with per-message thinking support
      const callbacks = {
        onStart: () => {
          log.debug("Enhanced streaming response started");
          setDirectResponseText(null);
        },

        onAiTraceId: (traceId: string) => {
          aiTraceId = traceId;
        },

        onChunk: (chunk: string) => {
          // First chunk the user could actually see, so this is the perceived
          // wait. Thinking chunks are counted too (they render as a live
          // segment), which is why this is not read off text chunks alone.
          firstChunkAt ??= Date.now();

          // Accumulate the response text
          currentResponse += chunk;

          // Update the assistant message content in real-time
          // Use agent-aware update to ensure updates go to correct agent
          updateMessageForAgent(capturedAgentId, assistantMessage.id, {
            content: currentResponse,
          });

          // Also update local state as backup for UI components
          setDirectResponseText(currentResponse);
        },

        // Each thinking_start opens a fresh inline thinking segment at the
        // current response offset. Chunks stream into its content live; on
        // stop the signature is recorded and the segment auto-collapses.
        onThinkingStart: () => {
          inlineToolEventCounter += 1;
          activeThinkingEvent = {
            id: `thinking-${assistantMessage.id}-${inlineToolEventCounter}`,
            type: "thinking",
            position: currentResponse.length,
            timestamp: Date.now(),
            data: {
              thinking: { content: "", signature: null, isStreaming: true },
            },
          };
          inlineToolEvents.push(activeThinkingEvent);
          updateMessageForAgent(capturedAgentId, assistantMessage.id, {
            inlineToolEvents: [...inlineToolEvents],
          });
        },

        onThinkingChunk: (chunk: string) => {
          firstChunkAt ??= Date.now();
          if (!activeThinkingEvent?.data.thinking) return;
          activeThinkingEvent.data.thinking.content += chunk;
          updateMessageForAgent(capturedAgentId, assistantMessage.id, {
            inlineToolEvents: [...inlineToolEvents],
          });
        },

        onThinkingComplete: (completedThinkingContent: string, signature: string | null) => {
          if (!activeThinkingEvent?.data.thinking) return;
          activeThinkingEvent.data.thinking.content = completedThinkingContent;
          activeThinkingEvent.data.thinking.signature = signature;
          activeThinkingEvent.data.thinking.isStreaming = false;
          updateMessageForAgent(capturedAgentId, assistantMessage.id, {
            inlineToolEvents: [...inlineToolEvents],
          });
          activeThinkingEvent = null;
        },

        // NEW: Web search callbacks
        onWebSearchToolUse: (toolInfo: ServerToolUse) => {
          if (toolInfo.name === "web_search") {
            webSearchState.isSearching = true;
            webSearchState.currentSearchNumber += 1;

            // Track that this search is awaiting a query (FIFO queue)
            searchesAwaitingQuery.push(webSearchState.currentSearchNumber);

            // Capture content position for inline rendering
            const positionInContent = currentResponse.length;
            webSearchPositionMap.set(webSearchState.currentSearchNumber, positionInContent);

            log.debug("Web search tool use detected", {
              messageId: assistantMessage.id,
              searchNumber: webSearchState.currentSearchNumber,
              position: positionInContent,
            });

            // Find or create webTools InlineToolEvent at this position
            const webToolsEvent = findOrCreateWebToolsEvent(
              inlineToolEvents,
              positionInContent,
              assistantMessage.id
            );
            webToolsEvent.data.isWebSearching = true;

            // Update message to show searching state
            updateMessageForAgent(capturedAgentId, assistantMessage.id, {
              isSearching: true,
              webSearchEvents: [...webSearchState.searchEvents],
              inlineToolEvents: [...inlineToolEvents],
            });
          } else if (toolInfo.name === "web_fetch") {
            webFetchToolUseCount += 1;
            const positionInContent = currentResponse.length;
            webFetchPositionMap.set(webFetchToolUseCount, positionInContent);

            log.debug("Web fetch tool use detected", {
              messageId: assistantMessage.id,
              fetchToolUseNumber: webFetchToolUseCount,
              position: positionInContent,
            });

            // Find or create webTools InlineToolEvent at this position
            const webToolsEvent = findOrCreateWebToolsEvent(
              inlineToolEvents,
              positionInContent,
              assistantMessage.id
            );
            webToolsEvent.data.isWebFetching = true;

            // Update message to show fetching state
            updateMessageForAgent(capturedAgentId, assistantMessage.id, {
              isFetching: true,
              inlineToolEvents: [...inlineToolEvents],
            });
          }
        },

        onWebSearchQuery: (query: WebSearchQuery) => {
          // FIX: Assign query to the oldest search awaiting one (FIFO queue)
          // This handles both batched arrivals and interleaved arrivals
          const searchNumber = searchesAwaitingQuery.shift();

          if (searchNumber !== undefined) {
            log.debug("Web search query received", {
              query: query.query,
              assignedToSearch: searchNumber,
              remainingQueue: searchesAwaitingQuery.length,
            });

            queryBySearchNumber.set(searchNumber, query.query);
            webSearchState.pendingQuery = query.query;
          } else {
            log.warn("Web search query arrived but no search is awaiting it", {
              query: query.query,
            });
          }
        },

        onWebSearchResults: (results: WebSearchResult[]) => {
          // Use eventCount + 1 instead of currentSearchNumber
          // currentSearchNumber reflects total tool_use events received (can be 4 when all arrive)
          // eventCount reflects how many result events we've processed (0, 1, 2, 3)
          const thisSearchNumber = webSearchState.searchEvents.length + 1;

          // Retrieve query for THIS specific search from the map
          const queryForThisSearch = queryBySearchNumber.get(thisSearchNumber) || "Web search";

          // If this search didn't receive a query, remove it from awaiting queue
          // This handles the case where a query is skipped (backend doesn't send input_json_delta)
          const queueIndex = searchesAwaitingQuery.indexOf(thisSearchNumber);
          if (queueIndex !== -1) {
            searchesAwaitingQuery.splice(queueIndex, 1);
            log.debug("Search completed without query, removed from queue", {
              searchNumber: thisSearchNumber,
              remainingQueue: searchesAwaitingQuery.length,
            });
          }

          log.debug("Web search results received", {
            resultCount: results.length,
            searchNumber: thisSearchNumber,
            query: queryForThisSearch,
          });

          // Create search event even without query (fallback to generic text)
          // Create new search event
          const searchEvent: WebSearchEvent = {
            id: `search-${assistantMessage.id}-${thisSearchNumber}`,
            searchNumber: thisSearchNumber,
            query: queryForThisSearch,
            results: results,
            isComplete: true,
          };

          // Add to events array
          webSearchState.searchEvents.push(searchEvent);

          // Attach search result to the correct InlineToolEvent
          const positionForSearch = webSearchPositionMap.get(thisSearchNumber);
          const position =
            positionForSearch !== undefined ? positionForSearch : currentResponse.length;
          const webToolsEvent = findOrCreateWebToolsEvent(
            inlineToolEvents,
            position,
            assistantMessage.id
          );
          webToolsEvent.data.webSearchEvents = [
            ...(webToolsEvent.data.webSearchEvents || []),
            searchEvent,
          ];
          // Only clear isWebSearching if no more searches are pending at this position
          const pendingAtPosition = [...webSearchPositionMap.entries()].filter(
            ([num, pos]) =>
              pos === position && !webSearchState.searchEvents.find((e) => e.searchNumber === num)
          );
          if (pendingAtPosition.length === 0) {
            webToolsEvent.data.isWebSearching = false;
          }

          // Update message with new search event
          // Only clear isSearching when all expected results have arrived
          const allSearchesResolved =
            webSearchState.searchEvents.length >= webSearchState.currentSearchNumber;

          // Use agent-aware update to ensure updates go to correct agent
          updateMessageForAgent(capturedAgentId, assistantMessage.id, {
            webSearchEvents: [...webSearchState.searchEvents],
            isSearching: !allSearchesResolved,
            inlineToolEvents: [...inlineToolEvents],
          });

          log.debug("Message updated with web search event", {
            messageId: assistantMessage.id,
            eventCount: webSearchState.searchEvents.length,
            latestQuery: searchEvent.query,
            resultCount: searchEvent.results.length,
            inlineEventCount: inlineToolEvents.length,
          });

          // Reset pending state for next search
          webSearchState.pendingQuery = undefined;
        },

        // NEW: Web fetch callback
        onWebFetchResult: (result: WebFetchResult) => {
          // Increment fetch number
          webFetchState.currentFetchNumber += 1;
          const thisFetchNumber = webFetchState.currentFetchNumber;

          // Enrich title from web search results if not provided
          let enrichedTitle = result.title;
          if (!enrichedTitle) {
            // Search through accumulated web search results for URL match
            for (const searchEvent of webSearchState.searchEvents) {
              const matchingResult = searchEvent.results.find((r) => r.url === result.url);
              if (matchingResult) {
                enrichedTitle = matchingResult.title;
                log.debug("Web fetch title enriched from search results", {
                  url: result.url,
                  originalTitle: result.title,
                  enrichedTitle: enrichedTitle,
                });
                break;
              }
            }
          }

          log.debug("Web fetch result received", {
            fetchNumber: thisFetchNumber,
            status: result.status,
            url: result.url,
            title: enrichedTitle || "No title",
            hasError: result.status === "error",
            errorCode: result.error_code,
          });

          // Create fetch event with enriched title
          const fetchEvent: WebFetchToolEvent = {
            id: `fetch-${assistantMessage.id}-${thisFetchNumber}`,
            toolType: "web_fetch",
            fetchNumber: thisFetchNumber,
            status: result.status,
            url: result.url,
            title: enrichedTitle,
            retrieved_at: result.retrieved_at,
            error_code: result.error_code,
            error_message: result.error_message,
          };

          // Add to events array
          webFetchState.fetchEvents.push(fetchEvent);

          // Attach fetch result to the correct InlineToolEvent
          const positionForFetch = webFetchPositionMap.get(thisFetchNumber);
          const position =
            positionForFetch !== undefined ? positionForFetch : currentResponse.length;
          const webToolsEvent = findOrCreateWebToolsEvent(
            inlineToolEvents,
            position,
            assistantMessage.id
          );
          webToolsEvent.data.webFetchEvents = [
            ...(webToolsEvent.data.webFetchEvents || []),
            fetchEvent,
          ];
          webToolsEvent.data.isWebFetching = false;

          // Update message with new fetch event
          // Use agent-aware update to ensure updates go to correct agent
          updateMessageForAgent(capturedAgentId, assistantMessage.id, {
            webFetchEvents: [...webFetchState.fetchEvents],
            isFetching: false,
            inlineToolEvents: [...inlineToolEvents],
          });

          log.debug("Message updated with web fetch event", {
            messageId: assistantMessage.id,
            agentId: capturedAgentId,
            totalFetchEvents: webFetchState.fetchEvents.length,
            latestEvent: fetchEvent,
            inlineEventCount: inlineToolEvents.length,
          });
        },

        // NEW: Auto-redline callback
        onAutoRedlineToolResult: (result: AutoRedlineOutput) => {
          // Record current position in content stream
          const positionInContent = currentResponse.length;
          const eventTimestamp = Date.now();

          // Add to outputs array
          autoRedlineOutputs.push(result);

          // Anchors the sibling-edit baseline for INSERT/DELETE outputs. Driven
          // from the stream rather than the rendered cards, and scoped to this
          // message — see appliedParagraphLedger.ts for both reasons.
          registerBaselineAnchor(result, assistantMessage.id);

          // Group tool calls by position: if no text was added since the last auto-redline
          // event, append to the same group. If text appeared in between, start a new group.
          const lastAutoRedlineEvent = [...inlineToolEvents]
            .reverse()
            .find((e) => e.type === "autoRedline");

          if (lastAutoRedlineEvent && lastAutoRedlineEvent.position === positionInContent) {
            // Same position — no text between calls, append to existing group
            lastAutoRedlineEvent.data.autoRedlineOutputs = [
              ...(lastAutoRedlineEvent.data.autoRedlineOutputs || []),
              result,
            ];
          } else {
            // New position — text was added between calls, create a new group
            inlineToolEventCounter++;
            const toolEvent: InlineToolEvent = {
              id: `autoRedline-${assistantMessage.id}-${inlineToolEventCounter}`,
              type: "autoRedline",
              position: positionInContent,
              timestamp: eventTimestamp,
              data: {
                autoRedlineOutputs: [result],
              },
            };
            inlineToolEvents.push(toolEvent);
          }

          // Update message with new auto-redline output and position
          // Also clear tool processing state since tool has completed
          // Use agent-aware update to ensure updates go to correct agent
          updateMessageForAgent(capturedAgentId, assistantMessage.id, {
            autoRedlineOutputs: [...autoRedlineOutputs],
            autoRedlinePosition: positionInContent, // Track where in content this was triggered (backward compat)
            // Clear tool processing state - tool has completed
            isProcessingTool: false,
            // NEW: Include unified inline tool events array
            inlineToolEvents: [...inlineToolEvents],
          });

          log.debug("Auto-redline output stored with position", {
            messageId: assistantMessage.id,
            sectionName: result.sectionName,
            editsCount: result.edits.length,
            positionInContent,
            inlineEventCount: inlineToolEvents.length,
          });
        },

        // NEW: Add-comment callback
        // One card per comment, no grouping. Status persists per-card so the
        // card survives remount and feeds memorySessionManager history.
        onAddCommentToolResult: (result: AddCommentOutput) => {
          const positionInContent = currentResponse.length;
          const eventTimestamp = Date.now();

          inlineToolEventCounter++;
          const toolEvent: InlineToolEvent = {
            id: `addComment-${assistantMessage.id}-${inlineToolEventCounter}`,
            type: "addComment",
            position: positionInContent,
            timestamp: eventTimestamp,
            data: {
              addComment: {
                result,
                status: "pending",
              },
            },
          };
          inlineToolEvents.push(toolEvent);

          // Clear tool processing state — the tool has completed.
          updateMessageForAgent(capturedAgentId, assistantMessage.id, {
            isProcessingTool: false,
            inlineToolEvents: [...inlineToolEvents],
          });

          log.debug("Add-comment result stored with position", {
            messageId: assistantMessage.id,
            sectionName: result.sectionName,
            paragraphId: result.paragraphId,
            hasAnchorPhrase: !!result.anchorPhrase,
            positionInContent,
            inlineEventCount: inlineToolEvents.length,
          });
        },

        // NEW: Form request callback
        onFormRequest: (payload: FormRequestPayload) => {
          log.info("Form request received", {
            messageId: assistantMessage.id,
            formType: payload.formType,
            formInstanceId: payload.formInstanceId,
            fieldCount: payload.fields.length,
          });

          // Store the form request payload
          formRequestPayload = payload;

          // Update message with form request payload
          updateMessageForAgent(capturedAgentId, assistantMessage.id, {
            formRequestPayload: payload,
          });

          log.debug("Form request stored on message", {
            messageId: assistantMessage.id,
            formType: payload.formType,
          });
        },

        // NEW: Playbook tool result callback
        onPlaybookToolResult: (result: NormalizedPlaybookResult) => {
          // Record current position in content stream
          const positionInContent = currentResponse.length;
          const eventTimestamp = Date.now();

          log.info("Playbook tool result received", {
            messageId: assistantMessage.id,
            contentLength: result.content_length,
            reason: result.reason,
          });

          // Store the playbook result
          playbookResult = result;

          // Persist the resolved playbook for the session so its id is sent on
          // subsequent requests. Covers backend auto-select (single playbook) and
          // fetch-by-id after the user picked one from a selection card.
          if (result.selected_playbook) {
            setSelectedPlaybook(result.selected_playbook);
            log.info("Selected playbook persisted from resolved content", {
              messageId: assistantMessage.id,
              playbookId: result.selected_playbook.id,
            });
          }

          // Create inline tool event for unified rendering
          inlineToolEventCounter++;
          const toolEvent: InlineToolEvent = {
            id: `playbook-${assistantMessage.id}-${inlineToolEventCounter}`,
            type: "playbook",
            position: positionInContent,
            timestamp: eventTimestamp,
            data: {
              playbookResult: result,
            },
          };
          inlineToolEvents.push(toolEvent);

          // Update message with playbook result and position
          // Also clear tool processing state since tool has completed
          updateMessageForAgent(capturedAgentId, assistantMessage.id, {
            playbookResult: result,
            playbookPosition: positionInContent, // Backward compatibility
            // Clear tool processing state - tool has completed
            isProcessingTool: false,
            // NEW: Include unified inline tool events array
            inlineToolEvents: [...inlineToolEvents],
          });

          log.debug("Playbook result stored on message", {
            messageId: assistantMessage.id,
            reason: result.reason,
            inlineEventCount: inlineToolEvents.length,
          });
        },

        // NEW: Playbook selection card callback
        // The backend offered a playbook picker instead of resolved content.
        // Render a selection card; when the user picks, handlePlaybookSelected
        // settles it and continues the same turn (the model then loads the choice).
        onPlaybookSelectionRequest: (result: PlaybookSelectionResult) => {
          const positionInContent = currentResponse.length;
          const eventTimestamp = Date.now();

          log.info("Playbook selection request received", {
            messageId: assistantMessage.id,
            playbookCount: result.playbooks.length,
            reason: result.reason,
          });

          inlineToolEventCounter++;
          const toolEvent: InlineToolEvent = {
            id: `playbookSelection-${assistantMessage.id}-${inlineToolEventCounter}`,
            type: "playbookSelection",
            position: positionInContent,
            timestamp: eventTimestamp,
            data: {
              playbookSelection: { result },
            },
          };
          inlineToolEvents.push(toolEvent);

          updateMessageForAgent(capturedAgentId, assistantMessage.id, {
            isProcessingTool: false,
            inlineToolEvents: [...inlineToolEvents],
          });

          log.debug("Playbook selection card stored on message", {
            messageId: assistantMessage.id,
            inlineEventCount: inlineToolEvents.length,
          });
        },

        // NEW: Catylex tool input callback (from input_json_delta) — lets the
        // processing indicator say "loading a contract" rather than "searching"
        // before the result arrives.
        onCatylexToolInput: (input: CatylexToolInput) => {
          log.info("Catylex tool action detected", {
            messageId: assistantMessage.id,
            action: input.action,
            query: input.query,
            contractId: input.contractId,
          });

          updateMessageForAgent(capturedAgentId, assistantMessage.id, {
            catylexToolInput: input,
          });
        },

        // NEW: Catylex search result callback
        onCatylexSearchResult: (result: NormalizedCatylexSearchResult) => {
          const positionInContent = currentResponse.length;
          const eventTimestamp = Date.now();

          log.info("Catylex tool result received", {
            messageId: assistantMessage.id,
            action: result.action,
            status: result.status,
            searchType: result.searchType,
            totalResults: result.totalResults,
            originalQuery: result.originalQuery,
            sentencesReturned: result.sentencesReturned,
          });

          // A cross-turn read carries no "View in Catylex" URL (see
          // findAtrUrlInSession) — recover it from the search that found this
          // contract. Build ONE enriched copy and store that everywhere: `result`
          // is otherwise shared by reference between message.catylexSearchResults
          // and the inline tool event's data, so mutating it in place would edit
          // an object already handed to us by the chunk router.
          let storedResult = result;
          if (result.action === "read" && !result.allTermsReportUrl) {
            const recoveredUrl = findAtrUrlInSession(
              currentSession.messages,
              result.contractId ?? ""
            );
            if (recoveredUrl) {
              storedResult = { ...result, allTermsReportUrl: recoveredUrl };
              log.info("Recovered Catylex report URL from an earlier search in this session", {
                messageId: assistantMessage.id,
                contractId: result.contractId,
              });
            }
          }

          catylexSearchResults.push(storedResult);

          // Create inline tool event for unified rendering
          inlineToolEventCounter++;
          const toolEvent: InlineToolEvent = {
            id: `catylexSearch-${assistantMessage.id}-${inlineToolEventCounter}`,
            type: "catylexSearch",
            position: positionInContent,
            timestamp: eventTimestamp,
            data: {
              catylexSearchResult: storedResult,
            },
          };
          inlineToolEvents.push(toolEvent);

          updateMessageForAgent(capturedAgentId, assistantMessage.id, {
            catylexSearchResults: [...catylexSearchResults],
            catylexSearchPosition: positionInContent,
            catylexToolInput: undefined,
            isProcessingTool: false,
            inlineToolEvents: [...inlineToolEvents],
          });

          log.debug("Catylex search result stored on message", {
            messageId: assistantMessage.id,
            resultCount: catylexSearchResults.length,
            inlineEventCount: inlineToolEvents.length,
          });
        },

        // NEW: SharePoint tool input callback (from input_json_delta)
        onSharePointToolInput: (input: SharePointToolInput) => {
          currentSharePointInput = input;

          log.info("SharePoint tool action detected", {
            messageId: assistantMessage.id,
            action: input.action,
            query: input.query,
          });

          updateMessageForAgent(capturedAgentId, assistantMessage.id, {
            sharepointToolInput: input,
          });
        },

        // NEW: SharePoint search result callback
        onSharePointSearchResult: (result: NormalizedSharePointResult) => {
          const positionInContent = currentResponse.length;
          const eventTimestamp = Date.now();

          log.info("SharePoint search result received", {
            messageId: assistantMessage.id,
            action: result.action,
            status: result.status,
          });

          // Attach the tool input to the result for conversation history reconstruction
          const resultWithInput = { ...result, toolInput: currentSharePointInput };
          sharepointSearchResults.push(resultWithInput);
          currentSharePointInput = undefined;

          // Create inline tool event for unified rendering
          inlineToolEventCounter++;
          const toolEvent: InlineToolEvent = {
            id: `sharepointSearch-${assistantMessage.id}-${inlineToolEventCounter}`,
            type: "sharepointSearch",
            position: positionInContent,
            timestamp: eventTimestamp,
            data: {
              sharepointSearchResult: resultWithInput,
            },
          };
          inlineToolEvents.push(toolEvent);

          updateMessageForAgent(capturedAgentId, assistantMessage.id, {
            sharepointSearchResults: [...sharepointSearchResults],
            sharepointSearchPosition: positionInContent,
            sharepointToolInput: undefined,
            isProcessingTool: false,
            inlineToolEvents: [...inlineToolEvents],
          });

          log.debug("SharePoint search result stored on message", {
            messageId: assistantMessage.id,
            resultCount: sharepointSearchResults.length,
            inlineEventCount: inlineToolEvents.length,
          });
        },

        // NEW: Outlook mail tool input callback (from input_json_delta)
        onOutlookMailToolInput: (input: OutlookMailToolInput) => {
          currentOutlookMailInput = input;

          log.info("Outlook mail tool action detected", {
            messageId: assistantMessage.id,
            action: input.action,
            subject: input.subject,
          });

          updateMessageForAgent(capturedAgentId, assistantMessage.id, {
            outlookMailToolInput: input,
          });
        },

        // NEW: Outlook mail result callback
        onOutlookMailResult: (result: OutlookMailResult) => {
          const positionInContent = currentResponse.length;
          const eventTimestamp = Date.now();

          log.info("Outlook mail result received", {
            messageId: assistantMessage.id,
            action: result.action,
            status: result.status,
          });

          outlookMailResults.push(result);
          currentOutlookMailInput = undefined;

          // Update document attachment state machine
          updateAttachmentState(result);

          // Create inline tool event for unified rendering
          inlineToolEventCounter++;
          const toolEvent: InlineToolEvent = {
            id: `outlookMail-${assistantMessage.id}-${inlineToolEventCounter}`,
            type: "outlookMail",
            position: positionInContent,
            timestamp: eventTimestamp,
            data: {
              outlookMailResult: result,
            },
          };
          inlineToolEvents.push(toolEvent);

          updateMessageForAgent(capturedAgentId, assistantMessage.id, {
            outlookMailResults: [...outlookMailResults],
            outlookMailToolInput: undefined,
            isProcessingTool: false,
            inlineToolEvents: [...inlineToolEvents],
          });

          log.debug("Outlook mail result stored on message", {
            messageId: assistantMessage.id,
            resultCount: outlookMailResults.length,
            inlineEventCount: inlineToolEvents.length,
          });
        },

        // NEW: Document citation callback
        onDocumentCitation: (citation: DocumentCitation) => {
          // Anthropic refs can arrive as JSON numbers; key by string so 0 and "0" match
          documentCitationsMap[String(citation.ref)] = citation;

          // Update message with accumulated citations
          updateMessageForAgent(capturedAgentId, assistantMessage.id, {
            documentCitations: { ...documentCitationsMap },
          });

          log.debug("Document citation stored in map", {
            messageId: assistantMessage.id,
            ref: citation.ref,
            mapSize: Object.keys(documentCitationsMap).length,
          });
        },

        onWebCitations: (insertions: WebCitationInsertion[]) => {
          webCitationInsertions.push(...insertions);
          // Google's fetch report has no page title, but the citations for the
          // same page do. Titled fetch events are new objects, so the card
          // re-renders on the completion update below; the web tools events
          // holding them get a new data object in place, as their spinner
          // flags already do.
          const pageTitles = new Map<string, string>();

          for (const insertion of insertions) {
            for (const source of insertion.sources) {
              if (source.page_title) pageTitles.set(source.url, source.page_title);
            }
          }
          const withTitle = (fetchEvent: WebFetchToolEvent): WebFetchToolEvent => {
            const pageTitle = pageTitles.get(fetchEvent.url);

            return pageTitle && !fetchEvent.title
              ? { ...fetchEvent, title: pageTitle }
              : fetchEvent;
          };

          if (pageTitles.size > 0) {
            webFetchState.fetchEvents = webFetchState.fetchEvents.map(withTitle);
            for (const evt of inlineToolEvents) {
              if (evt.type === "webTools" && evt.data.webFetchEvents) {
                evt.data = { ...evt.data, webFetchEvents: evt.data.webFetchEvents.map(withTitle) };
              }
            }
          }
        },

        // Tool use start notification - shows visual indicator while tool executes
        // NOTE: ToolProcessing is a TRANSIENT state, not added to inlineToolEvents
        // It shows at the end of content during processing and is replaced by actual tool result
        onToolUseStart: (toolName: string) => {
          const positionInContent = currentResponse.length;

          log.info("Tool processing started - updating UI", {
            messageId: assistantMessage.id,
            toolName,
            agentId: capturedAgentId,
            positionInContent,
          });

          updateMessageForAgent(capturedAgentId, assistantMessage.id, {
            isProcessingTool: true,
            toolProcessingPosition: positionInContent,
            processingToolName: toolName,
            toolProcessingStartTime: Date.now(),
          });
        },

        onComplete: (response: any) => {
          log.info("Enhanced streaming completed", {
            totalLength: response.content.length,
            sessionId: response.metadata?.sessionId,
            autoRedlineOutputsCount: autoRedlineOutputs.length,
            hasFormRequest: !!formRequestPayload,
            inlineToolEventsCount: inlineToolEvents.length,
            stopReason: response.metadata?.stopReason,
          });

          // Defensive: if the stream ended without a matching thinking_stop,
          // close the active segment so the UI doesn't show a stuck spinner.
          if (activeThinkingEvent?.data.thinking?.isStreaming) {
            activeThinkingEvent.data.thinking.isStreaming = false;
            activeThinkingEvent = null;
          }

          // Gemini's web citations arrive after the text they cite, so they are
          // spliced in here, once the text is final. Inline tool events are
          // placed by offsets into the same text and move with it.
          const webCitations = applyWebCitations(response.content, webCitationInsertions);
          if (webCitationInsertions.length > 0) {
            log.info("Web citations spliced into the answer", {
              received: webCitationInsertions.length,
              applied: webCitations.applied,
            });
          }

          // Track truncated responses for analytics (internal observability only)
          if (response.metadata?.stopReason === "max_tokens") {
            track("chat:response_truncated", {
              agent_id: capturedAgentId,
              stop_reason: "max_tokens",
              response_length: response.content.length,
              concise_mode_enabled: prepareUserPreferences.conciseMode ?? false,
              extended_thinking_enabled: prepareUserPreferences.extendedThinking ?? false,
              web_search_enabled: prepareUserPreferences.webSearch ?? false,
            });
          }

          // Response outcome. A healthy text stream always delivers a
          // message_stop chunk (the ONLY place metadata.stopReason is set), so
          // its absence normally means the connection closed mid-answer (proxy
          // timeout, server death). TWO deliberate exceptions: the backend
          // terminates workflow-form and playbook-selection turns right after
          // their interactive card chunk WITHOUT a message_stop (its
          // stop_reason was "tool_use" and the turn ends awaiting user input),
          // so a turn that produced such a card is a COMPLETED turn. And when
          // onError already fired (backend error chunk), the turn is already
          // counted as failed - emit nothing here.
          const endedWithInteractiveCard =
            !!formRequestPayload ||
            inlineToolEvents.some((evt) => evt.type === "playbookSelection");
          if (streamErrorReported) {
            // Outcome already recorded as chat:response_failed.
          } else if (response.metadata?.stopReason !== undefined || endedWithInteractiveCard) {
            const toolNames = [
              ...(autoRedlineOutputs.length > 0 ? ["auto_redline"] : []),
              ...(formRequestPayload ? ["workflow_form"] : []),
              ...(playbookResult ? ["playbook"] : []),
              ...(catylexSearchResults.length > 0 ? ["catylex_search"] : []),
              ...(sharepointSearchResults.length > 0 ? ["sharepoint_search"] : []),
              ...(outlookMailResults.length > 0 ? ["outlook_mail"] : []),
            ];
            track("chat:response_completed", {
              agent_id: capturedAgentId,
              response_length: response.content.length,
              thinking_used: inlineToolEvents.some((evt) => evt.type === "thinking"),
              web_search_count: webSearchState.searchEvents.length,
              web_fetch_count: webFetchState.fetchEvents.length,
              tool_names: toolNames.length > 0 ? toolNames : undefined,
              redline_suggestion_count:
                autoRedlineOutputs.length > 0 ? autoRedlineOutputs.length : undefined,
              // Interactive-card turns carry no message_stop; the model's
              // actual stop reason for them is "tool_use".
              stop_reason: response.metadata?.stopReason ?? "tool_use",
              ai_trace_id: aiTraceId,
              total_ms: Date.now() - turnStartedAt,
              first_chunk_ms: firstChunkAt ? firstChunkAt - turnStartedAt : undefined,
            });
          } else {
            track("chat:response_interrupted", {
              agent_id: capturedAgentId,
              received_chars: response.content.length,
              ai_trace_id: aiTraceId,
              total_ms: Date.now() - turnStartedAt,
              first_chunk_ms: firstChunkAt ? firstChunkAt - turnStartedAt : undefined,
            });
          }

          // Top of the redline funnel (received -> apply_clicked -> apply_completed).
          if (autoRedlineOutputs.length > 0) {
            track("redline:suggestions_received", {
              suggestion_count: autoRedlineOutputs.length,
              composer_mode: settings.composerMode,
            });
          }

          // Clear inline web tool spinner flags to prevent stuck spinners
          // (matches onError cleanup at lines below)
          for (const evt of inlineToolEvents) {
            if (evt.type === "webTools") {
              evt.data.isWebSearching = false;
              evt.data.isWebFetching = false;
            }
          }

          // Final update with all metadata
          // Use agent-aware update to ensure updates go to correct agent
          updateMessageForAgent(capturedAgentId, assistantMessage.id, {
            content: webCitations.content,
            // Finalize web search state
            isSearching: false,
            webSearchEvents: webSearchState.searchEvents,
            // Finalize web fetch state
            isFetching: false,
            webFetchEvents: webFetchState.fetchEvents,
            // Include auto-redline outputs
            autoRedlineOutputs: autoRedlineOutputs.length > 0 ? autoRedlineOutputs : undefined,
            // Include form request payload
            formRequestPayload: formRequestPayload || undefined,
            // Include playbook result
            playbookResult: playbookResult || undefined,
            // Include Catylex search results
            catylexSearchResults:
              catylexSearchResults.length > 0 ? catylexSearchResults : undefined,
            catylexToolInput: undefined,
            // Include SharePoint search results
            sharepointSearchResults:
              sharepointSearchResults.length > 0 ? sharepointSearchResults : undefined,
            sharepointToolInput: undefined,
            // Include Outlook mail results
            outlookMailResults: outlookMailResults.length > 0 ? outlookMailResults : undefined,
            outlookMailToolInput: undefined,
            // Finalize tool processing state (in case tool_use_result wasn't received)
            isProcessingTool: false,
            // NEW: Include unified inline tool events array
            inlineToolEvents:
              inlineToolEvents.length > 0
                ? webCitations.applied > 0
                  ? inlineToolEvents.map((evt) => ({
                      ...evt,
                      position: webCitations.shift(evt.position),
                    }))
                  : [...inlineToolEvents]
                : undefined,
            // Include document citations
            documentCitations:
              Object.keys(documentCitationsMap).length > 0
                ? { ...documentCitationsMap }
                : undefined,
          });

          // Ensure final state is consistent
          setDirectResponseText(webCitations.content);
          setBusy(false);
          if (!opts?.resume) {
            setPrompt(""); // Clear input after successful completion (not on a card resume)
          }

          // Clear pending message for this agent since it completed successfully
          setPendingMessages((prev) => {
            const newMap = new Map(prev);
            newMap.delete(capturedAgentId);
            return newMap;
          });
        },

        onError: (error: any) => {
          log.error("Streaming response failed", { error: error.message });

          // Product-side failure signal (the $exception twin lives in
          // captureError at the API layer; this one is for funnels/session
          // timelines, keyed to the agent that owned the request).
          streamErrorReported = true;
          track("chat:response_failed", {
            agent_id: capturedAgentId,
            error_code: error?.errorCode || "unknown",
            status_code: typeof error?.statusCode === "number" ? error.statusCode : undefined,
            received_chars: currentResponse.length,
            ai_trace_id: aiTraceId,
            total_ms: Date.now() - turnStartedAt,
          });

          // Reset thinking, search, and tool processing state on error
          // Also clear inline web tool spinner flags to prevent stuck spinners
          for (const evt of inlineToolEvents) {
            if (evt.type === "webTools") {
              evt.data.isWebSearching = false;
              evt.data.isWebFetching = false;
            }
          }

          // Close any thinking segment still streaming so it doesn't show a stuck spinner.
          if (activeThinkingEvent?.data.thinking?.isStreaming) {
            activeThinkingEvent.data.thinking.isStreaming = false;
            activeThinkingEvent = null;
          }

          // Use agent-aware update to ensure updates go to correct agent
          updateMessageForAgent(capturedAgentId, assistantMessage.id, {
            isSearching: false,
            isFetching: false,
            // Clear tool processing state on error, including any in-flight tool
            // input — it only drives the processing indicator, which is gone now.
            isProcessingTool: false,
            catylexToolInput: undefined,
            sharepointToolInput: undefined,
            outlookMailToolInput: undefined,
            inlineToolEvents: [...inlineToolEvents],
          });

          // Set error message directly from backend
          setError(error.message || "An error occurred during analysis");

          // Let a playbook-card continuation roll back its optimistic settle so the
          // card returns to an interactive, retryable state on failure.
          opts?.onResumeFailure?.();

          // Track context window overflow events for analytics (no UI impact)
          if (isContextWindowOverflow(error)) {
            const sessionMessages = currentSession.messages;
            const sessionWebFetchCount = sessionMessages.reduce(
              (sum, msg) => sum + (msg.webFetchEvents?.length || 0),
              0
            );
            const sessionWebSearchCount = sessionMessages.reduce(
              (sum, msg) => sum + (msg.webSearchEvents?.length || 0),
              0
            );

            track("chat:context_window_overflow", {
              agent_id: capturedAgentId,
              session_message_count: sessionMessages.length,
              session_web_fetch_count: sessionWebFetchCount,
              session_web_search_count: sessionWebSearchCount,
              web_tools_used: sessionWebFetchCount > 0 || sessionWebSearchCount > 0,
              error_code: error?.errorCode || "unknown",
            });
          }

          setBusy(false);

          // Clear pending message for this agent since error occurred
          setPendingMessages((prev) => {
            const newMap = new Map(prev);
            newMap.delete(capturedAgentId);
            return newMap;
          });
        },
      };

      // Execute the request through the model service with enhanced preferences
      await generateStreamingResponse(request, callbacks);
    } catch (e: any) {
      log.error("Unexpected error during analysis", { error: e.message || e });

      // Handle unexpected errors gracefully
      setError(e.message || "An unexpected error occurred during analysis");
      setBusy(false);
      opts?.onResumeFailure?.();
    }
  };

  /**
   * Handle the user picking a playbook from an in-chat selection card.
   *
   * Persists the choice and settles the card in place, then continues the same
   * turn: the list call (call #1) is closed in history with a tool_result naming
   * the choice, and the model makes a second call to load the chosen playbook —
   * whose content streams under the settled card as one coherent turn.
   * `selected_playbook_id` is read live at send time.
   *
   * Guards against double-submit: ignored if the card is already settled or
   * while a response is generating. On a continuation failure the card un-settles
   * so it stays retryable.
   *
   * @param messageId The assistant message holding the selection card
   * @param option The playbook the user chose
   */
  const handlePlaybookSelected = async (messageId: string, option: PlaybookSelectionOption) => {
    if (isGenerating || busy) {
      log.debug("Ignoring playbook selection - generation in progress", { messageId });
      return;
    }

    const capturedAgentId = currentAgent.id;
    const events =
      getAgentSession(capturedAgentId)?.messages.find((m) => m.id === messageId)
        ?.inlineToolEvents || [];

    // Double-submit guard: ignore if the selection card is already settled
    const selectionEvent = events.find(
      (evt) => evt.type === "playbookSelection" && evt.data.playbookSelection
    );
    if (selectionEvent?.data.playbookSelection?.selected) {
      log.debug("Ignoring playbook selection - card already settled", { messageId });
      return;
    }

    log.info("Playbook selected by user", {
      messageId,
      playbookId: option.id,
      playbookName: option.name,
    });

    // Settle (or un-settle) the card in place. Re-reads the message each call so
    // it works for both the optimistic settle and the rollback.
    const setSettled = (settled: boolean) => {
      const cur =
        getAgentSession(capturedAgentId)?.messages.find((m) => m.id === messageId)
          ?.inlineToolEvents || [];
      updateMessageForAgent(capturedAgentId, messageId, {
        inlineToolEvents: cur.map((evt) =>
          evt.type === "playbookSelection" && evt.data.playbookSelection
            ? {
                ...evt,
                data: {
                  ...evt.data,
                  playbookSelection: {
                    ...evt.data.playbookSelection,
                    selected: settled,
                    selectedId: settled ? option.id : undefined,
                    selectedName: settled ? option.name : undefined,
                  },
                },
              }
            : evt
        ),
      });
    };

    // 1) Persist the choice (read live into the next request's
    //    selected_playbook_id) and settle the card.
    setSelectedPlaybook({ id: option.id, name: option.name });
    setSettled(true);

    // 2) Continue the SAME turn. On failure, roll the card back to interactive.
    const rollback = () => {
      setSettled(false);
      setSelectedPlaybook(null);
    };
    await handleAnalyze(undefined, { resume: true, onResumeFailure: rollback });
  };

  /**
   * Clear chat history and reset state with Enhanced Thinking support
   *
   * Enhanced with proper session coordination and thinking state cleanup.
   */
  const handleClearChat = () => {
    try {
      log.info("Clearing chat session", {
        currentMessageCount: currentSession.messages.length,
        hasLastResponse: !!lastResponseText,
      });

      // Clear API service session first to ensure proper coordination
      try {
        clearSession();
        log.debug("API service session cleared");
      } catch (clearError) {
        log.warn("Could not clear API service session", {
          error: clearError instanceof Error ? clearError.message : String(clearError),
        });
      }

      // CS-259: Clear document-level index tracker for INSERT/DELETE operations
      // This prevents index adjustments from previous chat sessions affecting new ones
      try {
        clearDocumentIndexTracker();
        log.debug("Document index tracker cleared");
      } catch (trackerError) {
        log.warn("Could not clear document index tracker", {
          error: trackerError instanceof Error ? trackerError.message : String(trackerError),
        });
      }

      // Reset all local state
      setDirectResponseText(null);
      setLastResponseText(null);
      setError(null);
      setPrompt("");
      setBusy(false); // Reset busy state to fix streaming state bug

      // Clear the session through the session manager
      clearConversation();

      // And drop the stored copy, so the conversation does not come back the
      // next time the add-in is opened. Best-effort and idempotent.
      deleteStoredSession(currentAgent.id);

      log.info("Chat session cleared successfully");
    } catch (error) {
      log.error("Failed to clear chat session", { error });
      setError("Failed to clear chat history");
    }
  };

  const combinedError = error;

  // Return enhanced interface (removed conversation-level thinking state)
  return {
    // Existing state (unchanged for backward compatibility)
    prompt,
    setPrompt,
    busy,
    error: combinedError,
    setError,
    isGenerating,
    directResponseText,
    lastResponseText,
    currentSession,
    latestAssistantMessage,

    // Access to user preferences for components that need it
    userPreferences: prepareUserPreferences,

    // Existing actions (enhanced but backward compatible)
    handleAnalyze,
    handleClearChat,
    handlePlaybookSelected,

    // Add auth state for components that need it
    isAuthLoading: authLoading,
    hasAccessToken: !!accessToken,
  };
};
